import 'react-native-get-random-values';
import { Buffer } from 'buffer';
import React, { useState, useEffect } from 'react';
import { StatusBar } from 'expo-status-bar';
import { StyleSheet, View, ActivityIndicator, TouchableOpacity, Text, Platform, Dimensions } from 'react-native';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import * as SecureStore from 'expo-secure-store';
import TalkRoom from './src/screens/TalkRoom';
import ChatList from './src/screens/ChatList';
import Login from './src/screens/Login';
import Home from './src/screens/Home';
import Timeline from './src/screens/Timeline';
import PostDetail from './src/screens/PostDetail';
import Notifications from './src/screens/Notifications';
import Settings from './src/screens/Settings';
import Profile from './src/screens/Profile';
import Search from './src/screens/Search';
import UserList from './src/screens/UserList';
import SavedPosts from './src/screens/SavedPosts';
import Drafts from './src/screens/Drafts';
import LanguageSettings from './src/screens/LanguageSettings';
import FontSizeSettings from './src/screens/FontSizeSettings';
import FeedSearch from './src/screens/FeedSearch';
import SwipeBackWrapper from './src/components/SwipeBackWrapper';
import { login, getAccounts } from './src/services/bluesky';
import ThemeSettings from './src/screens/ThemeSettings.tsx';
import { Theme, ThemeProvider, FontSizeProvider, useThemedStyles, useTheme } from './src/theme';
import './src/i18n';
import { useTranslation } from 'react-i18next';

// Polyfill for Buffer
if (typeof global.Buffer === 'undefined') {
  global.Buffer = Buffer;
}

type Tab = 'home' | 'talk' | 'tl' | 'notifications' | 'others';

function MainContent({ isLoggedIn, setIsLoggedIn }: { isLoggedIn: boolean, setIsLoggedIn: (v: boolean) => void }) {
  const [activeTab, setActiveTab] = useState<Tab>('talk');
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [postStack, setPostStack] = useState<any[]>([]);
  const [profileActor, setProfileActor] = useState<string | null>(null);
  const [userListParams, setUserListParams] = useState<{ type: any, uri?: string, actor?: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSavedVisible, setIsSavedVisible] = useState(false);
  const [isDraftsVisible, setIsDraftsVisible] = useState(false);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [isThemeVisible, setIsThemeVisible] = useState(false);
  const [isLanguageVisible, setIsLanguageVisible] = useState(false);
  const [isFontSizeVisible, setIsFontSizeVisible] = useState(false);
  const [isFeedSearchVisible, setIsFeedSearchVisible] = useState(false);
  const [homeRefreshKey, setHomeRefreshKey] = useState(0);
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  if (!isLoggedIn || isAddingAccount) {
    return (
      <Login
        onLoginSuccess={() => {
          setIsLoggedIn(true);
          setIsAddingAccount(false);
          // Reset state to refresh content for new account
          setActiveTab('talk');
          setSelectedRoom(null);
          setPostStack([]);
          setProfileActor(null);
          setUserListParams(null);
        }}
        onCancel={isAddingAccount ? () => setIsAddingAccount(false) : undefined}
      />
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home': return (
        <Home 
          key={`home-${homeRefreshKey}`}
          onSearch={() => setIsSearchVisible(true)} 
          onSelectUser={(did) => setProfileActor(did)}
          onSelectFeed={(feed: any) => {
            setSelectedRoom({
              id: feed.uri,
              title: feed.displayName,
              subtitle: feed.description || 'Feed',
              avatarUrl: feed.avatar,
              type: 'feed',
              uri: feed.uri,
            });
          }}
          onSelectList={(list: any) => {
            setSelectedRoom({
              id: list.uri,
              title: list.name || list.displayName || 'List',
              subtitle: list.description || 'List',
              avatarUrl: list.avatar,
              type: 'list',
              uri: list.uri,
            });
          }}
          onOpenNotifications={() => setActiveTab('notifications')}
          onOpenSaved={() => setIsSavedVisible(true)}
          onOpenDrafts={() => setIsDraftsVisible(true)}
          onOpenFeedSearch={() => setIsFeedSearchVisible(true)}
        />
      );
      case 'talk': return (
        <ChatList 
          onSelectRoom={(room: any) => setSelectedRoom(room)} 
          onSearch={() => setIsSearchVisible(true)} 
          onNewMessage={() => {
            setSearchQuery(null);
            setIsSearchVisible(true);
          }}
          onOpenFeedSearch={() => setIsFeedSearchVisible(true)}
        />
      );
      case 'tl': return (
        <Timeline 
          onSelectPost={(post: any) => setPostStack([post])} 
          onSelectUser={(did: string) => setProfileActor(did)}
          onSearch={() => setIsSearchVisible(true)} 
          onSearchHashtag={(tag: string) => {
            setSearchQuery(tag);
            setIsSearchVisible(true);
          }}
          onSearchHandle={(handle: string) => {
            setSearchQuery(handle);
            setIsSearchVisible(true);
          }}
        />
      );
      case 'notifications': return (
        <Notifications 
          onSelectPost={(post: any) => setPostStack([post])} 
          onSelectUser={(did: string) => setProfileActor(did)}
          onSearchHashtag={(tag: string) => {
            setSearchQuery(tag);
            setIsSearchVisible(true);
          }}
        />
      );
      case 'others': return (
        <Settings 
          onLogout={() => setIsLoggedIn(false)} 
          onAddAccount={() => setIsAddingAccount(true)}
          onAccountSwitched={() => {
            // Reset state to refresh content for switched account
            setActiveTab('talk');
            setSelectedRoom(null);
            setPostStack([]);
            setProfileActor(null);
          }}
          onSelectProfile={(did) => setProfileActor(did)}
          onOpenSaved={() => setIsSavedVisible(true)}
          onOpenDrafts={() => setIsDraftsVisible(true)}
          onOpenTheme={() => setIsThemeVisible(true)}
          onOpenLanguage={() => setIsLanguageVisible(true)}
          onOpenFontSize={() => setIsFontSizeVisible(true)}
        />
      );
      default: return <ChatList onSelectRoom={(room: any) => setSelectedRoom(room)} onSearch={() => setIsSearchVisible(true)} />;
    }
  };
  const overlayStyle = { position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000, backgroundColor: theme.colors.overlay };

  return (
    <View style={styles.container}>
      <View style={{ flex: 1, marginBottom: Platform.OS === 'web' ? 60 : 0, backgroundColor: theme.colors.background }}>
        {renderTabContent()}
      </View>

      {/* Bottom Tab Bar */}
      <View style={[styles.tabBar, { paddingBottom: insets.bottom }]}>
        <TouchableOpacity style={styles.tabItem} onPress={() => setActiveTab('home')}>
          <Ionicons name={activeTab === 'home' ? 'home' : 'home-outline'} size={24} color={activeTab === 'home' ? theme.colors.primary : theme.colors.textMuted} />
          <Text style={[styles.tabLabel, { color: activeTab === 'home' ? theme.colors.primary : theme.colors.textMuted }]}>{t('tabs.home')}</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.tabItem} onPress={() => setActiveTab('talk')}>
          <Ionicons name={activeTab === 'talk' ? 'chatbubble' : 'chatbubble-outline'} size={24} color={activeTab === 'talk' ? theme.colors.primary : theme.colors.textMuted} />
          <Text style={[styles.tabLabel, { color: activeTab === 'talk' ? theme.colors.primary : theme.colors.textMuted }]}>{t('tabs.talk')}</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.tabItem} onPress={() => setActiveTab('tl')}>
          <MaterialCommunityIcons name={activeTab === 'tl' ? 'view-dashboard' : 'view-dashboard-outline'} size={24} color={activeTab === 'tl' ? theme.colors.primary : theme.colors.textMuted} />
          <Text style={[styles.tabLabel, { color: activeTab === 'tl' ? theme.colors.primary : theme.colors.textMuted }]}>{t('tabs.timeline')}</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.tabItem} onPress={() => setActiveTab('notifications')}>
          <Ionicons name={activeTab === 'notifications' ? 'notifications' : 'notifications-outline'} size={24} color={activeTab === 'notifications' ? theme.colors.primary : theme.colors.textMuted} />
          <Text style={[styles.tabLabel, { color: activeTab === 'notifications' ? theme.colors.primary : theme.colors.textMuted }]}>{t('tabs.notifications')}</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.tabItem} onPress={() => setActiveTab('others')}>
          <Ionicons name={activeTab === 'others' ? 'ellipsis-horizontal' : 'ellipsis-horizontal-outline'} size={24} color={activeTab === 'others' ? theme.colors.primary : theme.colors.textMuted} />
          <Text style={[styles.tabLabel, { color: activeTab === 'others' ? theme.colors.primary : theme.colors.textMuted }]}>{t('tabs.others')}</Text>
        </TouchableOpacity>
      </View>

      {/* Overlays (rendered above base content so swipe reveals underlying tab) */}
      {selectedRoom && (
        <View style={overlayStyle}>
          <SwipeBackWrapper 
            onBack={() => setSelectedRoom(null)}
            enabled={Platform.OS !== 'web'}
          >
            <TalkRoom 
              room={selectedRoom} 
              onBack={() => setSelectedRoom(null)} 
              onSelectUser={(did: string) => setProfileActor(did)}
              onSelectPost={(post: any) => setPostStack([post])}
              onSearchHashtag={(tag: string) => {
                setSearchQuery(tag);
                setIsSearchVisible(true);
              }}
              onSearchHandle={(handle: string) => {
                setSearchQuery(handle);
                setIsSearchVisible(true);
              }}
              onSettings={() => {
                setSelectedRoom(null);
                setActiveTab('others');
              }}
            />
          </SwipeBackWrapper>
        </View>
      )}

      {isDraftsVisible && (
        <View style={overlayStyle}>
          <SwipeBackWrapper
            onBack={() => setIsDraftsVisible(false)}
            enabled={Platform.OS !== 'web'}
          >
            <Drafts 
              onBack={() => setIsDraftsVisible(false)}
            />
          </SwipeBackWrapper>
        </View>
      )}

      {isThemeVisible && (
        <View style={overlayStyle}>
          <SwipeBackWrapper
            onBack={() => setIsThemeVisible(false)}
            enabled={Platform.OS !== 'web'}
          >
            <ThemeSettings onBack={() => setIsThemeVisible(false)} />
          </SwipeBackWrapper>
        </View>
      )}

      {isLanguageVisible && (
        <View style={overlayStyle}>
          <SwipeBackWrapper
            onBack={() => setIsLanguageVisible(false)}
            enabled={Platform.OS !== 'web'}
          >
            <LanguageSettings onBack={() => setIsLanguageVisible(false)} />
          </SwipeBackWrapper>
        </View>
      )}

      {isFontSizeVisible && (
        <View style={overlayStyle}>
          <SwipeBackWrapper
            onBack={() => setIsFontSizeVisible(false)}
            enabled={Platform.OS !== 'web'}
          >
            <FontSizeSettings onBack={() => setIsFontSizeVisible(false)} />
          </SwipeBackWrapper>
        </View>
      )}

      {isFeedSearchVisible && (
        <View style={overlayStyle}>
          <SwipeBackWrapper
            onBack={() => setIsFeedSearchVisible(false)}
            enabled={Platform.OS !== 'web'}
          >
            <FeedSearch 
              onBack={() => setIsFeedSearchVisible(false)}
              onFeedAdded={() => setHomeRefreshKey(prev => prev + 1)}
              onSelectFeed={(feed: any) => {
                setIsFeedSearchVisible(false);
                setSelectedRoom({
                  id: feed.uri,
                  title: feed.displayName,
                  subtitle: feed.description || 'Feed',
                  avatarUrl: feed.avatar,
                  type: 'feed',
                  uri: feed.uri,
                });
              }}
            />
          </SwipeBackWrapper>
        </View>
      )}

      {postStack.length > 0 && !profileActor && (
        <View style={overlayStyle}>
          <SwipeBackWrapper 
            onBack={() => setPostStack(prev => prev.slice(0, -1))}
            enabled={Platform.OS !== 'web'}
          >
            <PostDetail 
              post={postStack[postStack.length - 1]} 
              onBack={() => setPostStack(prev => prev.slice(0, -1))}
              onSelectPost={(post: any) => setPostStack(prev => [...prev, post])}
              onSelectUser={(did: string) => setProfileActor(did)}
              onShowUserList={(type: string, uri: string) => setUserListParams({ type, uri })}
              onSearchHashtag={(tag: string) => {
                setSearchQuery(tag);
                setIsSearchVisible(true);
              }}
              onSearchHandle={(handle: string) => {
                setSearchQuery(handle);
                setIsSearchVisible(true);
              }}
            />
          </SwipeBackWrapper>
        </View>
      )}

      {profileActor && (
        <View style={overlayStyle}>
          <SwipeBackWrapper 
            onBack={() => setProfileActor(null)}
            enabled={Platform.OS !== 'web'}
          >
            <Profile 
              actor={profileActor} 
              onBack={() => setProfileActor(null)}
              onSelectPost={(post) => setPostStack([post])}
              onShowUserList={(type, actor) => setUserListParams({ type, actor })}
              onSelectRoom={(room: any) => {
                setProfileActor(null);
                setPostStack([]);
                setIsSearchVisible(false);
                setUserListParams(null);
                setSelectedRoom(room);
              }}
              onSearchHashtag={(tag: string) => {
                setSearchQuery(tag);
                setIsSearchVisible(true);
              }}
              onSearchHandle={(handle: string) => {
                setSearchQuery(handle);
                setIsSearchVisible(true);
              }}
            />
          </SwipeBackWrapper>
        </View>
      )}

      {postStack.length > 0 && profileActor && (
        <View style={overlayStyle}>
          <SwipeBackWrapper 
            onBack={() => setPostStack(prev => prev.slice(0, -1))}
            enabled={Platform.OS !== 'web'}
          >
            <PostDetail 
              post={postStack[postStack.length - 1]} 
              onBack={() => setPostStack(prev => prev.slice(0, -1))}
              onSelectPost={(post: any) => setPostStack(prev => [...prev, post])}
              onSelectUser={(did: string) => setProfileActor(did)}
              onShowUserList={(type: string, uri: string) => setUserListParams({ type, uri })}
              onSearchHashtag={(tag: string) => {
                setSearchQuery(tag);
                setIsSearchVisible(true);
              }}
              onSearchHandle={(handle: string) => {
                setSearchQuery(handle);
                setIsSearchVisible(true);
              }}
            />
          </SwipeBackWrapper>
        </View>
      )}

      {userListParams && (
        <View style={overlayStyle}>
          <SwipeBackWrapper 
            onBack={() => setUserListParams(null)}
            enabled={Platform.OS !== 'web'}
          >
            <UserList 
              {...userListParams}
              onBack={() => setUserListParams(null)}
              onSelectUser={(did) => {
                setUserListParams(null);
                setProfileActor(did);
              }}
              onSelectPost={(post) => {
                setUserListParams(null);
                setPostStack(prev => [...prev, post]);
              }}
            />
          </SwipeBackWrapper>
        </View>
      )}

      {/* Global Overlays (placed at the bottom to stay on top of Profile/PostDetail) */}
      {isSearchVisible && (
        <View style={overlayStyle}>
          <SwipeBackWrapper 
            onBack={() => { setIsSearchVisible(false); setSearchQuery(null); }}
            enabled={Platform.OS !== 'web'}
          >
            <Search 
              onBack={() => { setIsSearchVisible(false); setSearchQuery(null); }}
              initialQuery={searchQuery || ''}
              onSelectPost={(post) => { setIsSearchVisible(false); setPostStack([post]); }}
              onSelectUser={(did) => { setIsSearchVisible(false); setProfileActor(did); }}
              onSearchHashtag={(tag) => setSearchQuery(tag)}
              onSelectRoom={(room: any) => {
                setIsSearchVisible(false);
                setSearchQuery(null);
                setProfileActor(null);
                setPostStack([]);
                setUserListParams(null);
                setSelectedRoom(room);
              }}
            />
          </SwipeBackWrapper>
        </View>
      )}

      {isSavedVisible && (
        <View style={overlayStyle}>
          <SwipeBackWrapper
            onBack={() => setIsSavedVisible(false)}
            enabled={Platform.OS !== 'web'}
          >
            <SavedPosts 
              onBack={() => setIsSavedVisible(false)}
              onSelectPost={(post) => {
                setIsSavedVisible(false);
                setPostStack([post]);
              }}
              onSelectUser={(did) => {
                setIsSavedVisible(false);
                setProfileActor(did);
              }}
            />
          </SwipeBackWrapper>
        </View>
      )}
    </View>
  );
}

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const { theme } = useTheme();
  const styles = useThemedStyles(createStyles);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const accounts = await getAccounts();
      const activeAccount = accounts.find(a => a.active);

      if (activeAccount) {
        await login(activeAccount.identifier, activeAccount.password);
        setIsLoggedIn(true);
      } else {
        // Fallback to legacy keys if no accounts list exists yet
        const identifier = await (await import('./src/utils/storage')).getItem('bsky_identifier');
        const password = await (await import('./src/utils/storage')).getItem('bsky_password');

        if (identifier && password) {
          await login(identifier, password);
          setIsLoggedIn(true);
        }
      }
    } catch (error) {
      console.error('Auto login failed:', error);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center', backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <>
      <StatusBar style={theme.isDark ? 'light' : 'dark'} />
      <MainContent isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />
    </>
  );
}

export default function App() {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <ThemeProvider>
        <FontSizeProvider>
          <SafeAreaProvider>
            <AppContent />
          </SafeAreaProvider>
        </FontSizeProvider>
      </ThemeProvider>
    </GestureHandlerRootView>
  );
}

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
    ...(Platform.OS === 'web' ? { height: Dimensions.get('window').height } : {}),
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: theme.colors.card,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.border,
    paddingTop: 8,
    alignItems: 'center',
    ...(Platform.OS === 'web' ? {
      position: 'fixed' as any,
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
    } : {}),
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabLabel: {
    fontSize: 10,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  tabLabelActive: {
    color: theme.colors.primary,
  },
});
